/* Studio Mottos — single visual header. The existing site header remains
   unchanged when the native modal opens; modal controls overlay its icons. */
(() => {
  if (window.smMobileNavigationV2) return;
  window.smMobileNavigationV2 = true;
  const wide = matchMedia('(min-width: 1000px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new WeakMap();
  function initialize(scope = document) {
    const dialog = scope.querySelector('dialog.sm-mobile-menu');
    if (!dialog || instances.has(dialog)) return;
    const controller = new AbortController();
    instances.set(dialog, controller);
    const signal = controller.signal;
    const panels = [...dialog.querySelectorAll('[data-sm-mobile-panel]')];
    const title = dialog.querySelector('#sm-mobile-menu-title');
    const back = dialog.querySelector('[data-sm-mobile-back]');
    const toolbar = dialog.querySelector('.sm-mobile-menu__toolbar');
    const closeButton = dialog.querySelector('[data-sm-mobile-close]');
    const header = dialog.closest('x-header');
    const menuButton = header?.querySelector('[aria-controls="sidebar-menu"]');
    const actions = dialog.querySelector('.sm-mobile-menu__header-actions');
    const brand = dialog.querySelector('.sm-mobile-menu__brand');
    const liveLogo = header?.querySelector('.header__logo a, a.header__logo');
    let opener, animation, current = 'main';
    const announcementHost = dialog.querySelector('[data-sm-mobile-announcements]');
    const announcementPositions = [];
    function restoreAnnouncements() {
      announcementPositions.splice(0).forEach(({section,marker}) => marker.replaceWith(section));
      if (announcementHost) announcementHost.hidden = true;
    }
    // Announcement remains at its original position so the header never shifts.
    function resetPanels() {
      panels.forEach(panel => { panel.scrollTop = 0; });
      dialog.querySelectorAll('.sm-mobile-menu__accordion').forEach(details => { details.open = false; });
    }
    function preparePanel(panel) {
      [...panel.querySelectorAll('img')].slice(0,8).forEach(img => { img.fetchPriority='auto'; img.loading='eager'; });
    }
    function navigate(name, focus = true) {
      const next = panels.find(panel => panel.dataset.smMobilePanel === name);
      if (!next) return;
      preparePanel(next); resetPanels();
      const goingBack = name === 'main';
      animation?.cancel();
      panels.forEach(panel => { panel.hidden = panel !== next; });
      current = name;
      dialog.dataset.smMobileScreen = name;
      toolbar.hidden = goingBack;
      title.textContent = {main:'Menu',art:'Art Prints',artists:'Artists'}[name];
      back.hidden = goingBack;
      dialog.querySelectorAll('[data-sm-mobile-next]').forEach(button => button.setAttribute('aria-expanded', String(button.dataset.smMobileNext === name)));
      if (focus) title.focus({preventScroll:true});
      if (focus && !reduced.matches && next.animate) animation = next.animate([{opacity:.4,transform:`translateX(${goingBack ? -20 : 20}px)`},{opacity:1,transform:'translateX(0)'}],{duration:180,easing:'ease-out'});
    }
    function close() { if (dialog.open) dialog.close(); }
    // Overlay only transparent click targets for the REAL header's right-side actions.
    // The visual logo/search/cart are never duplicated or moved.
    function positionOverlay() {
      if (!header || !menuButton || !dialog.open) return;
      const toggle = menuButton.getBoundingClientRect();
      const headerBox = header.querySelector('.header')?.getBoundingClientRect() || header.getBoundingClientRect();
      dialog.style.setProperty('--sm-live-header-bottom', `${Math.max(0, headerBox.bottom)}px`);
      dialog.style.setProperty('--sm-live-toggle-left', `${toggle.left}px`);
      dialog.style.setProperty('--sm-live-toggle-top', `${toggle.top}px`);
      dialog.style.setProperty('--sm-live-toggle-width', `${toggle.width}px`);
      dialog.style.setProperty('--sm-live-toggle-height', `${toggle.height}px`);
      if (brand && liveLogo) {
        const rect = liveLogo.getBoundingClientRect();
        brand.style.left = `${rect.left}px`;
        brand.style.top = `${rect.top}px`;
        brand.style.width = `${rect.width}px`;
        brand.style.height = `${rect.height}px`;
      }
      if (actions) {
        const search = header.querySelector('.header__search-link a');
        const cart = header.querySelector('.header__cart-link a');
        for (const [kind, target] of [['search',search],['cart',cart]]) {
          const proxy = actions.querySelector(`[data-sm-mobile-header-action="${kind}"]`);
          if (!proxy) continue;
          if (!target) { proxy.hidden = true; continue; }
          const rect = target.getBoundingClientRect();
          proxy.hidden = false;
          proxy.style.left = `${rect.left}px`;
          proxy.style.top = `${rect.top}px`;
          proxy.style.width = `${rect.width}px`;
          proxy.style.height = `${rect.height}px`;
        }
      }
    }
    document.addEventListener('click', event => {
      const button = event.target.closest('[aria-controls="sidebar-menu"]');
      if (!button || wide.matches || !dialog.contains(button) && !header?.contains(button)) return;
      event.preventDefault(); opener = button;
      navigate('main',false);
      dialog.showModal();
      document.documentElement.classList.add('sm-mobile-menu-open');
      button.setAttribute('aria-expanded','true');
      positionOverlay();
      title.focus({preventScroll:true});
    }, {signal});
    dialog.addEventListener('click', event => {
      if (event.target.closest('[data-sm-mobile-close]')) return close();
      if (event.target.closest('[data-sm-mobile-back]')) return navigate('main');
      const action = event.target.closest('[data-sm-mobile-header-action]');
      if (action) {
        const selector = action.dataset.smMobileHeaderAction === 'search' ? '.header__search-link a' : '.header__cart-link a';
        const trigger = header?.querySelector(selector);
        event.preventDefault(); event.stopPropagation();
        close();
        if (trigger) trigger.click();
        else if (action.href) location.href = action.href;
        return;
      }
      const next = event.target.closest('[data-sm-mobile-next]');
      if (next) return navigate(next.dataset.smMobileNext);
      const link = event.target.closest('a[href]');
      if (link) {
        const destination = new URL(link.href, location.href);
        if (destination.origin === location.origin && destination.pathname === location.pathname && destination.search === location.search && destination.hash) close();
      }
    }, {signal});
    dialog.addEventListener('close', () => {
      animation?.cancel(); resetPanels(); restoreAnnouncements();
      document.documentElement.classList.remove('sm-mobile-menu-open');
      opener?.setAttribute('aria-expanded','false');
      if (opener?.isConnected && !wide.matches) opener.focus({preventScroll:true});
    }, {signal});
    window.addEventListener('resize',positionOverlay,{signal});
    window.addEventListener('pageshow',event => {if(event.persisted)close();},{signal});
    wide.addEventListener('change',()=>{if(wide.matches)close();},{signal});
    dialog.querySelectorAll('[data-sm-mobile-next]').forEach(button => button.setAttribute('aria-expanded','false'));
  }
  initialize();
  document.addEventListener('shopify:section:load',event=>initialize(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    const dialog=event.target.querySelector('dialog.sm-mobile-menu');
    if(!dialog)return;
    if(dialog.open)dialog.close();
    instances.get(dialog)?.abort(); instances.delete(dialog);
    document.documentElement.classList.remove('sm-mobile-menu-open');
  });
})();
